/**
 * vue-class-state v0.4.1
 * (c) 2020 zetaplus006
 * @license MIT
 */
import Vue from 'vue';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function assert(condition, msg) {
    if (!condition)
        throw new Error("[vue-class-state warn] " + msg);
}
var _hasOwn = Object.prototype.hasOwnProperty;
function hasOwn(obj, key) {
    return _hasOwn.call(obj, key);
}
var def = Object.defineProperty;
function hideProperty(obj, key, value) {
    def(obj, key, {
        value: value,
        enumerable: false,
        configurable: true
    });
}
function defGet(obj, key, get) {
    def(obj, key, {
        get: get,
        enumerable: true,
        configurable: true
    });
}
function assign(target, source) {
    var key;
    for (key in source) {
        if (hasOwn(source, key)) {
            target[key] = source[key];
        }
    }
    return target;
}
var isDev = process.env.NODE_ENV !== 'production';

var classMetaKey = '__meta__';
var ClassMetaData = /** @class */ (function () {
    function ClassMetaData() {
        this.injectParameterMeta = [];
        this.getterKeys = [];
    }
    ClassMetaData.get = function (target) {
        if (hasOwn(target.constructor, classMetaKey)) {
            return target.constructor[classMetaKey];
        }
        var meta = new ClassMetaData();
        hideProperty(target.constructor, classMetaKey, meta);
        return meta;
    };
    ClassMetaData.prototype.addGetterKey = function (key) {
        if (this.getterKeys.indexOf(key) === -1) {
            this.getterKeys.push(key);
        }
    };
    return ClassMetaData;
}());

var meta_key = '__meta__';
var DIMetaData = /** @class */ (function () {
    function DIMetaData() {
        this.hasBeenInjected = false;
    }
    DIMetaData.get = function (ctx) {
        if (!ctx[meta_key]) {
            hideProperty(ctx, meta_key, new DIMetaData());
        }
        return ctx[meta_key];
    };
    return DIMetaData;
}());

var BaseInjector = /** @class */ (function () {
    function BaseInjector() {
    }
    BaseInjector.prototype.resolve = function () {
        return this.instance || (this.instance = this.getInstance());
    };
    BaseInjector.prototype.addDIMeta = function (instance, identifier) {
        var meta = DIMetaData.get(instance);
        if (meta.hasBeenInjected) {
            return;
        }
        meta.identifier = identifier;
        meta.provider = this.provider;
        this.provider.hooks.forEach(function (fn) { return fn(instance, meta); });
        meta.hasBeenInjected = true;
    };
    return BaseInjector;
}());
var ClassInjector = /** @class */ (function (_super) {
    __extends(ClassInjector, _super);
    function ClassInjector(identifier, stateClass) {
        var _this = _super.call(this) || this;
        _this.identifier = identifier;
        _this.stateClass = stateClass;
        return _this;
    }
    ClassInjector.prototype.getInstance = function () {
        var instance = resolveClassInstance(this.provider, this);
        this.addDIMeta(instance, this.identifier);
        return instance;
    };
    return ClassInjector;
}(BaseInjector));
var ValueInjector = /** @class */ (function (_super) {
    __extends(ValueInjector, _super);
    function ValueInjector(identifier, state) {
        var _this = _super.call(this) || this;
        _this.identifier = identifier;
        _this.state = state;
        return _this;
    }
    ValueInjector.prototype.getInstance = function () {
        this.addDIMeta(this.state, this.identifier);
        return this.state;
    };
    return ValueInjector;
}(BaseInjector));
var FactoryInjector = /** @class */ (function (_super) {
    __extends(FactoryInjector, _super);
    function FactoryInjector(identifier, stateFactory, deps) {
        var _this = _super.call(this) || this;
        _this.identifier = identifier;
        _this.stateFactory = stateFactory;
        _this.deps = deps;
        return _this;
    }
    FactoryInjector.prototype.getInstance = function () {
        var args = this.provider.getAll(this.deps);
        var instance = this.stateFactory.apply(null, args);
        this.addDIMeta(instance, this.identifier);
        return instance;
    };
    return FactoryInjector;
}(BaseInjector));
function resolveClassInstance(provider, injector) {
    var classMeta = ClassMetaData.get(injector.stateClass.prototype);
    var parameterMeta = classMeta.injectParameterMeta;
    var args = provider.getAll(parameterMeta);
    return new ((_a = injector.stateClass).bind.apply(_a, [void 0].concat(args)))();
    var _a;
}

var Binding = /** @class */ (function () {
    function Binding(identifier) {
        this.identifier = identifier;
    }
    Binding.prototype.toClass = function (stateClass) {
        var _this = this;
        this.injectorFactory = function () {
            return new ClassInjector(_this.identifier, stateClass);
        };
        return this;
    };
    Binding.prototype.toValue = function (state) {
        var _this = this;
        this.injectorFactory = function () {
            return new ValueInjector(_this.identifier, state);
        };
        return this;
    };
    Binding.prototype.toFactory = function (factory, deps) {
        var _this = this;
        if (deps === void 0) { deps = []; }
        this.injectorFactory = function () {
            return new FactoryInjector(_this.identifier, factory, deps);
        };
        return this;
    };
    return Binding;
}());
function bind(identifier) {
    return new Binding(identifier);
}

var scopeKey = '__scope__';
var ScopeData = /** @class */ (function () {
    function ScopeData() {
        this.$state = {};
        this.$getters = {};
    }
    ScopeData.get = function (ctx) {
        return ctx[scopeKey] || (function () {
            var scope = new ScopeData();
            hideProperty(ctx, scopeKey, scope);
            return scope;
        })();
    };
    return ScopeData;
}());

var devtoolHook = typeof window !== 'undefined' &&
    window['__VUE_DEVTOOLS_GLOBAL_HOOK__'];
function devtool(container, identifiers) {
    var provider = container._provider;
    if (!devtoolHook)
        return;
    var store = simulationStore(provider, identifiers);
    devtoolHook.emit('vuex:init', store);
    devtoolHook.on('vuex:travel-to-state', function (targetState) {
        provider.replaceAllState(targetState);
    });
}
function simulationStore(provider, identifiers) {
    var _a = getStateAndGetters(provider.proxy, identifiers), state = _a.state, getters = _a.getters;
    var store = {
        state: state,
        getters: getters,
        _devtoolHook: devtoolHook,
        // tslint:disable-next-line:no-empty
        registerModule: function () { },
        // tslint:disable-next-line:no-empty
        unregisterModule: function () { },
        replaceState: function (targetState) {
            provider.replaceAllState(targetState);
        },
        _vm: new Vue({}),
        _mutations: {}
    };
    return store;
}
function getStateAndGetters(proxy, identifiers) {
    var getters = {};
    var state = {};
    var keys = identifiers;
    keys.forEach(function (key) {
        var instance = proxy[key];
        var scope = ScopeData.get(instance);
        tryReadGetters(instance);
        def(getters, String(key), {
            value: scope.$getters,
            enumerable: true,
            configurable: true
        });
        def(state, String(key), {
            value: scope.$state,
            enumerable: true,
            configurable: true
        });
    });
    return {
        state: state,
        getters: getters
    };
}
/**
 * try to read the first getter
 * @param instance
 */
function tryReadGetters(instance, proto) {
    if (proto && proto === Object.prototype) {
        return;
    }
    var _proto = Object.getPrototypeOf(proto || instance);
    var getterKeys = ClassMetaData.get(_proto).getterKeys;
    var len = getterKeys.length;
    try {
        while (len--) {
            instance[getterKeys[len]];
        }
        // tslint:disable-next-line:no-empty
    }
    finally { }
    tryReadGetters(instance, _proto);
}

// tslint:disable-next-line:no-empty
var noop = function () { };
var isSSR = Vue.prototype.$isServer;
var globalState = {
    middlewares: [],
    isCommitting: false
};
if (process.env.NODE_ENV !== 'production' && devtoolHook) {
    globalState.middlewares.push(function (next, mutation, state) {
        var result = next();
        devtoolHook.emit('vuex:mutation', mutation, state);
        return result;
    });
}

/**
 * change from https://github.com/koajs/compose/blob/master/index.js
 */
function compose(middlewares) {
    return function (next, mutation, state) {
        var index = -1;
        return dispatch(0);
        function dispatch(i) {
            if (i <= index)
                throw new Error('next() called multiple times');
            index = i;
            var fn = middlewares[i];
            if (i === middlewares.length)
                fn = next;
            if (!fn)
                return;
            try {
                // tslint:disable-next-line:no-shadowed-variable
                return fn(function next() {
                    return dispatch(i + 1);
                }, mutation, state);
            }
            catch (err) {
                throw err;
            }
        }
    };
}

var Mutation = assign(createMutation(), { create: createMutation });
function createMutation() {
    var middleware = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        middleware[_i] = arguments[_i];
    }
    var cb;
    var args = [];
    var commitFn = compose(middleware.concat(globalState.middlewares)
        .concat(function (_next, _mutation, state) {
        var result = allowChange(function () { return cb && cb.apply(state, args); });
        return result;
    }));
    function commit() {
        var commitArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            commitArgs[_i] = arguments[_i];
        }
        var state;
        var mutationType;
        if (typeof commitArgs[0] === 'function') {
            state = undefined;
            cb = commitArgs[0];
            mutationType = commitArgs[1];
            args = commitArgs[2] || [];
        }
        else {
            state = commitArgs[0];
            cb = commitArgs[1];
            mutationType = commitArgs[2];
            args = commitArgs[3] || [];
        }
        return commitFn(null, createMuationData(state, mutationType, args), state);
    }
    function decorator(_target, methodName, descriptor) {
        var mutationFn = descriptor.value;
        descriptor.value = function () {
            var arg = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                arg[_i] = arguments[_i];
            }
            return commit(this, mutationFn, methodName, arg);
        };
        return descriptor;
    }
    return assign(decorator, { commit: commit });
}
var unnamedName = '<unnamed mutation>';
var unknownIdentifier = 'unknown';
function createMuationData(ctx, mutationType, payload) {
    var meta = ctx && ctx[meta_key], identifier = meta && meta.identifier || unknownIdentifier, mType = mutationType || unnamedName, type = identifier + ': ' + mType;
    var mutation = {
        type: type,
        payload: payload,
        mutationType: mType,
        identifier: identifier
    };
    return mutation;
}
var allowChange = process.env.NODE_ENV !== 'production'
    ? function (cb) {
        var temp = globalState.isCommitting;
        globalState.isCommitting = true;
        var result = cb();
        globalState.isCommitting = temp;
        return result;
    }
    : function (cb) { return cb(); };

var StateDecorator = isSSR
    // tslint:disable-next-line:no-empty
    ? noop
    : function (target, propertyKey) {
        def(target, propertyKey, {
            get: function () {
                if (process.env.NODE_ENV !== 'production') {
                    assert(false, "This property [" + propertyKey + "] must be initialized");
                }
            },
            set: function (value) {
                var _this = this;
                Vue.util.defineReactive(this, propertyKey, value);
                var scopeData = ScopeData.get(this);
                if (isDev) {
                    def(scopeData.$state, propertyKey, {
                        get: function () { return _this[propertyKey]; },
                        set: function (val) {
                            _this[propertyKey] = val;
                        },
                        enumerable: true,
                        configurable: true
                    });
                }
            },
            enumerable: true,
            configurable: true
        });
    };
function replaceState(targetState, state) {
    var scope = targetState[scopeKey] || undefined;
    if (scope === undefined)
        return;
    allowChange(function () {
        for (var key in state) {
            if (hasOwn(targetState, key)) {
                targetState[key] = state[key];
            }
        }
    });
}
function getAllState(state) {
    return ScopeData.get(state).$state;
}
var State = assign(StateDecorator, {
    replaceState: replaceState,
    getAllState: getAllState
});

var _watcher, _dep, Watcher, Dep;
if (!isSSR) {
    var computedKey_1 = 'c';
    var vm = new Vue({
        data: {
            a: 1
        },
        computed: (_a = {}, _a[computedKey_1] = function () {
                return 1;
            }, _a)
    });
    vm.$destroy();
    _watcher = vm._computedWatchers[computedKey_1];
    _dep = vm._data.__ob__.dep;
    Watcher = Object.getPrototypeOf(_watcher).constructor;
    Dep = Object.getPrototypeOf(_dep).constructor;
}
var _a;

/**
 * change from https://github.com/vuejs/vue/blob/dev/src/core/instance/state.js#L163
 */
var defaultComputedOption = { enumerable: false };
var computedWatcherOptions = {
    lazy: true,
    computed: true
};
function Computed(targetOrOption, propertyKey) {
    if (propertyKey) {
        return createComputed(defaultComputedOption, targetOrOption, propertyKey);
    }
    else {
        return function (target, key) {
            return createComputed(targetOrOption, target, key);
        };
    }
}
var watcherKey = '_watchers';
var createComputed = isSSR
    // tslint:disable-next-line:no-empty
    ? noop
    : _createComputed;
function _createComputed(option, target, propertyKey) {
    var desc = Object.getOwnPropertyDescriptor(target, propertyKey);
    if (process.env.NODE_ENV !== 'production') {
        assert(desc && desc.get, '[@Getter] must be used for getter property');
    }
    var get = desc.get;
    ClassMetaData.get(target).addGetterKey(propertyKey);
    return {
        get: function () {
            var _this = this;
            var scope = ScopeData.get(this);
            if (!this[watcherKey]) {
                hideProperty(this, watcherKey, []);
            }
            var watcher = new Watcher(this, get, noop, computedWatcherOptions);
            var getter = createComputedGetter(watcher);
            def(this, propertyKey, {
                get: getter,
                enumerable: option.enumerable,
                configurable: true
            });
            if (isDev) {
                defGet(scope.$getters, propertyKey, function () { return _this[propertyKey]; });
            }
            return getter();
        },
        enumerable: option.enumerable,
        configurable: true
    };
}
function createComputedGetter(watcher) {
    return function computedGetter() {
        if (watcher.dirty) {
            watcher.evaluate();
        }
        if (Dep.target) {
            watcher.depend();
        }
        return watcher.value;
    };
}

function Inject(identifier) {
    return function (target, propertyKey, parameterIndex) {
        if (typeof parameterIndex === 'number') {
            setParamsMeta(target, parameterIndex, identifier);
        }
        else {
            if (target instanceof Vue) {
                injectIntoComponent(target, propertyKey, identifier);
            }
            else {
                return lazyDecorator(propertyKey, identifier);
            }
        }
    };
}
function setParamsMeta(target, index, identifier) {
    var classMeta = ClassMetaData.get(target.prototype);
    classMeta.injectParameterMeta[index] = identifier;
}
function lazyDecorator(propertyKey, identifier) {
    var stateKey = identifier || propertyKey;
    return {
        get: function () {
            var state = DIMetaData.get(this).provider.get(stateKey);
            hideProperty(this, propertyKey, state);
            return state;
        },
        enumerable: false,
        configuriable: true
    };
}
/**
 * change from https://github.com/vuejs/vue-class-component/blob/master/src/util.ts#L19
 * and https://github.com/kaorun343/vue-property-decorator/blob/master/src/vue-property-decorator.ts#L19
 */
function injectIntoComponent(proto, propertyKey, identifier) {
    var Ctor = proto.constructor;
    if (!Ctor.__decorators__) {
        Ctor.__decorators__ = [];
    }
    var factory = function (option, key) {
        if (option.inject === undefined) {
            option.inject = {};
        }
        if (!Array.isArray(option.inject)) {
            option.inject[key] = identifier;
        }
    };
    Ctor.__decorators__.push(function (options) { return factory(options, propertyKey); });
}

function useStrict(state) {
    var identifier = DIMetaData.get(state).identifier, scope = state[scopeKey] || undefined;
    if (scope && Watcher) {
        if (!state[watcherKey]) {
            hideProperty(state, watcherKey, []);
        }
        new Watcher(state, function () {
            return scope.$state;
        }, function () {
            assert(globalState.isCommitting, "Do not mutate state[" + identifier + "] data outside mutation handlers.");
        }, { deep: true, sync: true });
    }
}

var SimpleMap = /** @class */ (function () {
    function SimpleMap() {
        this.dictionary = Object.create(null);
    }
    SimpleMap.prototype.get = function (key) {
        return this.dictionary[key] || undefined;
    };
    SimpleMap.prototype.set = function (key, value) {
        this.dictionary[key] = value;
        return this;
    };
    SimpleMap.prototype.has = function (key) {
        return hasOwn(this.dictionary, key);
    };
    return SimpleMap;
}());
var UseMap = typeof Map === 'function' ? Map : SimpleMap;

var Provider = /** @class */ (function () {
    function Provider(proxyObj) {
        this.hooks = [];
        this.injectorMap = new UseMap();
        this.proxy = proxyObj;
    }
    /**
     * get state instance
     * @param identifier
     */
    Provider.prototype.get = function (identifier) {
        var injector = this.injectorMap.get(identifier);
        if (process.env.NODE_ENV !== 'production') {
            assert(injector, String(identifier) + " not find in provider");
        }
        return injector.resolve();
    };
    /**
     * get state instance array
     * @param deps
     */
    Provider.prototype.getAll = function (deps) {
        var _this = this;
        return deps.map(function (identifier) { return _this.get(identifier); });
    };
    /**
     * register a injector in the provider
     * @param injector
     */
    Provider.prototype.register = function (injector) {
        this.checkIdentifier(injector.identifier);
        injector.provider = this;
        this.injectorMap.set(injector.identifier, injector);
        this.defProxy(injector);
    };
    Provider.prototype.checkIdentifier = function (identifier) {
        if (process.env.NODE_ENV !== 'production') {
            assert(!this.injectorMap.has(identifier), "The identifier " + String(identifier) + " has been repeated");
        }
    };
    /**
     * replaceState for SSR and devtool
     * @param proxyState
     */
    Provider.prototype.replaceAllState = function (proxyState) {
        for (var key in proxyState) {
            var instance = this.proxy[key];
            replaceState(instance, proxyState[key]);
        }
    };
    Provider.prototype.registerInjectedHook = function (injected) {
        if (this.hooks.indexOf(injected) > -1) {
            return;
        }
        this.hooks.push(injected);
    };
    /**
     * for vue provide option
     * @param injector
     */
    Provider.prototype.defProxy = function (injector) {
        defGet(this.proxy, injector.identifier, function () {
            return injector.resolve();
        });
    };
    return Provider;
}());

// import { devtool } from '../dev/devtool';
function Container(option) {
    return function (_target) {
        return createContainerClass(option);
    };
}
function createContainerClass(option) {
    return /** @class */ (function () {
        function $StateModule() {
            var _this = this;
            hideProperty(this, '_provider', new Provider(this));
            hideProperty(this, '_globalPlugins', option.globalPlugins || []);
            hideProperty(this, '_option', option);
            var storeIdentifiers = option.providers.map(function (binding) {
                _this._provider.register(binding.injectorFactory());
                return binding.identifier;
            });
            var strictList = option.strict === true ? storeIdentifiers : (option.strict || []);
            var devtoolList = option.devtool === false
                ? [] : Array.isArray(option.devtool)
                ? option.devtool : storeIdentifiers;
            this._provider.registerInjectedHook(function (instance, diMetaData) {
                if (process.env.NODE_ENV !== 'production' && !isSSR) {
                    if (instance[scopeKey] && !diMetaData.hasBeenInjected
                        && strictList.indexOf(diMetaData.identifier) > -1) {
                        useStrict(instance);
                    }
                }
                _this._globalPlugins.forEach(function (action) { return action(instance); });
            });
            if (process.env.NODE_ENV !== 'production' && devtoolList.length) {
                devtool(this, devtoolList);
            }
        }
        return $StateModule;
    }());
}

/**
 * vue-class-state interface
 */

export { bind, State, Computed as Getter, Inject, Mutation, Container };
